import { useEffect } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import AddressPage from "./pages/AddressPage.js";
import VehiclePage from "./pages/VehiclePage.js";
import ReviewPage from "./pages/ReviewPage.js";
import { useLang, useT } from "./i18n/LanguageContext.js";
import "./styles.css";

export default function App() {
  const { lang, setLang } = useLang();
  const t = useT();
  const location = useLocation();
  const navigate = useNavigate();

  // Pick up ?lang= from URL and sync language
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const qLang = params.get("lang");
    if (qLang && (qLang === "en" || qLang === "fr")) setLang(qLang);
  }, [location.search, setLang]);

  // Move focus to main on route change (A11Y)
  useEffect(() => {
    const m = document.getElementById("main");
    if (m) m.focus();
  }, [location.pathname, location.search]);

  // helper to update only lang in the query, keep path and other params
  const updateLang = (val) => {
    const params = new URLSearchParams(location.search);
    params.set("lang", val);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  const inAddress = location.pathname === "/" || location.pathname.startsWith("/address");
  const inVehicle = location.pathname.startsWith("/vehicle");
  const inReview = location.pathname.startsWith("/review");

  return (
    <div className="container">
      <a href="#main" className="skip-link">{t("skipToMain")}</a>

      <header className="header" role="banner">
        <div className="brand">
          <div className="brand-logo" aria-hidden="true"></div>
          <h1>{t("brandTitle")}</h1>
        </div>

        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <nav aria-label="Progress" className="stepper">
            <div className="step" aria-current={inAddress ? "step" : undefined}>
              <div className="dot">1</div><div className="label">{t("steps.address")}</div>
            </div>
            <div className="step" aria-current={inVehicle ? "step" : undefined}>
              <div className="dot">2</div><div className="label">{t("steps.vehicle")}</div>
            </div>
            <div className="step" aria-current={inReview ? "step" : undefined}>
              <div className="dot">3</div><div className="label">{t("steps.review")}</div>
            </div>
          </nav>

          <label htmlFor="lang" className="sr-only">{t("language")}</label>
          <select
            id="lang"
            value={lang}
            onChange={(e) => { updateLang(e.target.value); }}
          >
            <option value="en">{t("english")}</option>
            <option value="fr">{t("french")}</option>
          </select>
        </div>
      </header>

      <main id="main" role="main" tabIndex="-1" className="card">
        <Routes>
          {/* Address */}
          <Route path="/" element={<AddressPage />} />
          <Route path="/address" element={<AddressPage />} />
          <Route path="/address/:productType" element={<AddressPage />} />

          {/* Vehicle */}
          <Route path="/vehicle" element={<VehiclePage />} />
          <Route path="/vehicle/:productType" element={<VehiclePage />} />

          {/* Review */}
          <Route path="/review" element={<ReviewPage />} />

          {/* Fallback */}
          <Route path="*" element={<AddressPage />} />
        </Routes>
      </main>

      <footer role="contentinfo" style={{ padding: "14px 0", color: "var(--muted)" }}>
        <small>{t("footer")}</small>
      </footer>
    </div>
  );
}
